import type { LiveSlotHost } from "@hobbyshopify/backend-client";
import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { ShareButton } from "../../components/ShareButton";
import { formatWhen } from "../../core/primitives/format";
import { breakBadge, formatCents, formatMultiplier } from "../../core/primitives/live";
import { fillBadge } from "../live-streams/live-streams-model";
import { LiveBreakActions, LiveBreakState } from "./live-break-model";

function spotStatus(slot: LiveSlotHost): string {
  if (slot.status === "SOLD") return slot.buyerName ?? "Sold";
  if (slot.status === "HELD") return "In a checkout";
  if (slot.status === "HOUSE") return "House";
  return "";
}

export function LiveBreakPage() {
  const state = useSubtree(LiveBreakState);
  const actions = useSubtree(LiveBreakActions);
  const observer = useObserver();

  const status = observer.watch(state.status);
  const b = observer.watch(state.breakRow);
  const share = observer.watch(state.share);
  const slots = observer.watch(state.slots);
  const title = observer.watch(state.title);
  const saveTitle = observer.watch(state.saveTitle);
  const breakTime = observer.watch(state.breakTime);
  const saveBreakTime = observer.watch(state.saveBreakTime);
  const cover = observer.watch(state.cover);
  const confirmCancel = observer.watch(state.confirmCancel);
  const cancel = observer.watch(state.cancel);
  const promote = observer.watch(state.promote);

  if (!b) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <Link to="/live" className="text-sm opacity-60 hover:opacity-100">
          ← Live breaks
        </Link>
        {status.kind === "error" ? (
          <p className="note note-danger">{status.message}</p>
        ) : (
          <p className="opacity-60">Loading the break…</p>
        )}
      </div>
    );
  }

  const over = b.status === "COMPLETED" || b.status === "CANCELLED";
  const badge = b.status === "ON_SALE" ? fillBadge(b.soldCount, b.slotCount) : breakBadge(b.status);
  const pct = b.slotCount === 0 ? 0 : Math.round((b.soldCount / b.slotCount) * 100);
  const nextPromote = b.promotedAt ? new Date(b.promotedAt.getTime() + PROMOTE_INTERVAL_MS) : null;
  const promoteWaiting = nextPromote !== null && nextPromote.getTime() > Date.now();
  const titleChanged = title.trim() !== "" && title.trim() !== b.title;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4">
      <Link to="/live" className="text-sm opacity-60 hover:opacity-100">
        ← Live breaks
      </Link>

      <header className="flex flex-wrap items-center gap-3">
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold">{b.title}</h1>
        {b.segment && !over && (
          <Link to={`/live/streams/${b.segment.streamId}`} className="btn-secondary btn-sm">
            Open the stream
          </Link>
        )}
        {share && (
          <ShareButton
            target={share}
            heading={b.status === "COMPLETED" ? "Share the results" : "Share this break"}
            note="Buyers can grab a spot on hobbyshop.ai or in the HobbyScan app. It fills faster when it is shared."
          />
        )}
      </header>

      <section className="panel space-y-3 p-4">
        <div className="panel-head">What buyers see</div>

        <div className="flex flex-wrap items-start gap-4">
          {b.imageUrl ? (
            <img src={b.imageUrl} alt="" className="h-40 w-30 shrink-0 rounded object-cover" />
          ) : (
            <div className="flex h-40 w-30 shrink-0 items-center justify-center rounded bg-white/5 text-xs opacity-50">
              No image
            </div>
          )}

          <div className="min-w-0 flex-1 space-y-3">
            <label className="block">
              <span className="label">Name</span>
              <input
                className="field"
                value={title}
                onChange={(e) => actions.setTitle(e.target.value)}
                maxLength={200}
                disabled={over}
              />
            </label>
            {!over && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={!titleChanged || saveTitle.busy}
                  onClick={() => void actions.saveTitle()}
                >
                  {saveTitle.busy ? "Saving…" : "Save name"}
                </button>
                {saveTitle.saved && !titleChanged && <span className="text-xs opacity-60">{saveTitle.saved}</span>}
              </div>
            )}
            {saveTitle.error && <p className="note note-danger">{saveTitle.error}</p>}

            {!over && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="btn-secondary btn-sm cursor-pointer">
                  {cover.busy ? "Uploading…" : b.imageUrl ? "Replace image" : "Choose an image"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={cover.busy}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void actions.uploadCover(file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            )}
            {cover.error && <p className="note note-danger">{cover.error}</p>}
          </div>
        </div>
      </section>

      {!over && (
        <section className="panel space-y-3 p-4">
          <div className="panel-head">Break time</div>
          <p className="text-sm opacity-60">
            Not earlier than this; empty means any time. Spot holders are told whenever it moves. Once a spot sells it
            moves later freely, earlier only to two hours away, and cannot be cleared.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              className="field w-auto"
              value={breakTime}
              onChange={(e) => actions.setBreakTime(e.target.value)}
            />
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={saveBreakTime.busy}
              onClick={() => void actions.saveBreakTime()}
            >
              {saveBreakTime.busy ? "Saving…" : "Save"}
            </button>
            {saveBreakTime.saved && <span className="text-sm opacity-60">{saveBreakTime.saved}</span>}
          </div>
          {saveBreakTime.error && <p className="note note-danger">{saveBreakTime.error}</p>}
        </section>
      )}

      <section className="panel space-y-3 p-4">
        <div className="panel-head">Sales</div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-[color:var(--color-accent)]" style={{ width: `${pct}%` }} />
          </div>
          <span className="shrink-0 text-xs tabular-nums opacity-60">{pct}%</span>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
          <Figure label="Spots sold" value={`${b.soldCount}/${b.slotCount}`} />
          <Figure label="Taken" value={formatCents(b.grossSoldCents)} />
          <Figure label="Price per spot" value={formatCents(b.defaultPriceCents)} />
          <Figure label="What it cost" value={formatCents(b.costCents)} />
          <Figure label="What it is worth" value={formatCents(b.marketValueCents)} />
          <Figure label="Target" value={formatMultiplier(b.targetMultiplierPct)} />
          <Figure label="Profit if it sells out" value={formatCents(b.profitIfSoldOutCents)} />
          {b.sealedProductName && <Figure label="From inventory" value={b.sealedProductName} />}
        </dl>
      </section>

      {b.status === "ON_SALE" && (
        <section className="panel space-y-3 p-4">
          <div className="panel-head">Promote</div>
          <p className="text-sm opacity-70">
            Send a push to everyone with the HobbyScan app that this box still has spots. Once a day.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={promote.busy || promoteWaiting}
              onClick={() => void actions.promote()}
            >
              {promote.busy ? "Sending…" : "Promote"}
            </button>
            {promote.saved && <span className="text-sm opacity-60">{promote.saved}</span>}
            {!promote.saved && promoteWaiting && nextPromote && (
              <span className="text-sm opacity-60">Next promote {formatWhen(nextPromote)}</span>
            )}
          </div>
          {promote.error && <p className="note note-danger">{promote.error}</p>}
        </section>
      )}

      <section className="panel space-y-3 p-4">
        <div className="panel-head">The spots</div>
        <div className="max-h-96 overflow-y-auto rounded-row border border-line">
          <table className="w-full text-sm">
            <tbody>
              {slots.map((slot) => (
                <tr key={slot.id} className="border-b border-line/50 last:border-b-0">
                  <td className="w-10 px-2 py-1 text-xs opacity-50 tabular-nums">{slot.slotNumber}</td>
                  <td className="px-2 py-1">{slot.assignedLabel ?? b.labelPool[slot.slotNumber - 1] ?? ""}</td>
                  <td className="px-2 py-1 text-xs opacity-70">{spotStatus(slot)}</td>
                  <td className="w-24 px-2 py-1 text-right text-xs tabular-nums">{formatCents(slot.priceCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {!over && (
        <section className="panel space-y-3 p-4">
          <div className="panel-head">Cancel this break</div>
          {confirmCancel ? (
            <>
              <p className="note note-warn">
                {b.soldCount > 0
                  ? `${b.soldCount} paid spot${b.soldCount === 1 ? " is" : "s are"} refunded in full, postage included.`
                  : "Nobody has bought a spot yet."}{" "}
                Checkouts in progress are dropped{b.sealedProductName ? " and the box goes back on the shelf" : ""}.
                This cannot be undone.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-destructive btn-sm"
                  disabled={cancel.busy}
                  onClick={() => void actions.cancelBreak()}
                >
                  {cancel.busy ? "Cancelling…" : "Yes, cancel it"}
                </button>
                <button type="button" className="btn-ghost btn-sm" disabled={cancel.busy} onClick={actions.keepBreak}>
                  Keep it
                </button>
              </div>
            </>
          ) : (
            <div>
              <button type="button" className="btn-secondary btn-sm" onClick={() => void actions.cancelBreak()}>
                Cancel break
              </button>
            </div>
          )}
          {cancel.error && <p className="note note-danger">{cancel.error}</p>}
        </section>
      )}
      {over && cancel.error && <p className="note note-danger">{cancel.error}</p>}
    </div>
  );
}

const PROMOTE_INTERVAL_MS = 24 * 60 * 60 * 1000;

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs opacity-50">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
